import { useState } from "react";

export function SettingsEditor({ settings, onSave }) {
  const [s, setS] = useState(settings);
  const setV = (k, v) => setS(p => ({ ...p, [k]: v }));
  const setLabel = (k, v) => setS(p => ({ ...p, labels: { ...(p.labels || {}), [k]: v } }));
  const num = (k, label) => (
    <div className="border border-[#E5E7EB] p-3 bg-white">
      <div className="label-eyebrow mb-1">{label}</div>
      <input data-testid={`setting-${k}`} type="number" step="0.01" value={s[k] ?? 0} onChange={e => setV(k, parseFloat(e.target.value))} className="w-full border-b border-[#E5E7EB] py-1" />
    </div>
  );
  return (
    <div>
      <div className="mb-3">
        <div className="label-eyebrow">Business &amp; booking configuration</div>
        <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">Quote-engine item prices now live in the <strong>Pricing &amp; market benchmarks</strong> panel at the top of this page. The fields below control thresholds, promotions, capacity, VAT, deposit and planning limits.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {num("ulez_default_working_days", "ULEZ default days")}
        {num("free_bc_threshold", "Free BC threshold")}
        {num("discount_threshold", "Tier discount threshold")}
        {num("discount_pct", "Tier discount %")}
        {num("launch_offer_pct", "Summer offer %")}
        {num("launch_offer_days", "Summer offer days")}
        {num("min_lead_weeks", "Min lead weeks")}
        {num("booking_months_ahead", "Booking horizon (months)")}
        {num("monthly_capacity", "Monthly capacity")}
        {num("min_weeks_between_starts", "Weeks between starts")}
        {num("express_min_business_days", "Express min working days")}
        {num("express_fee", "Express fee")}
        {num("vat_rate", "VAT rate")}
        {num("deposit_pct", "Deposit %")}
        {num("pd_depth_detached", "PD depth detached")}
        {num("pd_depth_semi_terraced", "PD depth semi/terraced")}
        {num("pd_max_width", "PD max width")}
        {num("planning_max_depth", "Planning max depth")}
        {num("planning_max_width", "Planning max width")}
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="border border-[#E5E7EB] p-3 bg-white">
          <div className="label-eyebrow mb-1">Summer offer start date (YYYY-MM-DD)</div>
          <input data-testid="setting-launch-date" value={s.launch_date || ""} onChange={e => setV("launch_date", e.target.value)} className="w-full border-b border-[#E5E7EB] py-1" />
        </div>
        <div className="border border-[#C79A3B] p-3 bg-[#FBF7EF]">
          <div className="label-eyebrow mb-1">Autumn offer · FREE doors until (YYYY-MM-DD)</div>
          <input data-testid="setting-free-doors-until" value={s.free_doors_until || ""} onChange={e => setV("free_doors_until", e.target.value)} className="w-full border-b border-[#E5E7EB] py-1 bg-transparent" />
          <div className="text-[11px] text-[#6B7280] mt-1">Bifold (any door) refunded in full on quotes/bookings before this date. Blank = off.</div>
        </div>
        <div className="border border-[#C79A3B] p-3 bg-[#FBF7EF]">
          <div className="label-eyebrow mb-1">Autumn offer · No booking fee until (YYYY-MM-DD)</div>
          <input data-testid="setting-site-visit-until" value={s.site_visit_mode_until || ""} onChange={e => setV("site_visit_mode_until", e.target.value)} className="w-full border-b border-[#E5E7EB] py-1 bg-transparent" />
          <div className="text-[11px] text-[#6B7280] mt-1">Clients book a free 1-hour site visit instead of paying £499. Blank = off (normal £499 flow).</div>
        </div>
        <div className="border border-[#E5E7EB] p-3 bg-white grid grid-cols-2 gap-3">
          {num("site_visit_slot_start", "Site visit first slot (hour)")}
          {num("site_visit_slot_end", "Site visit last slot ends (hour)")}
        </div>
      </div>
      <div className="mt-6 border border-[#E5E7EB] p-3 bg-white">
        <div className="label-eyebrow mb-2">Serviceable outward codes</div>
        <PostcodeChips value={s.serviceable_outward_codes || []} onChange={v => setV("serviceable_outward_codes", v)} />
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="border border-[#E5E7EB] p-3 bg-white">
          <div className="label-eyebrow mb-1">Blackout start (MM-DD)</div>
          <input data-testid="setting-blackout-start" value={s.blackout_start || ""} onChange={e => setV("blackout_start", e.target.value)} className="w-full border-b border-[#E5E7EB] py-1" />
        </div>
        <div className="border border-[#E5E7EB] p-3 bg-white">
          <div className="label-eyebrow mb-1">Blackout end (MM-DD)</div>
          <input data-testid="setting-blackout-end" value={s.blackout_end || ""} onChange={e => setV("blackout_end", e.target.value)} className="w-full border-b border-[#E5E7EB] py-1" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="border border-[#E5E7EB] p-3 bg-white">
          <div className="label-eyebrow mb-1">Main phone line</div>
          <input data-testid="setting-main-phone" value={s.main_phone || ""} onChange={e => setV("main_phone", e.target.value)} className="w-full border-b border-[#E5E7EB] py-1" />
        </div>
        <div className="border border-[#E5E7EB] p-3 bg-white">
          <div className="label-eyebrow mb-1">Blocked start months (YYYY-MM, comma separated)</div>
          <input data-testid="setting-blocked-months" value={(s.blocked_start_months || []).join(", ")} onChange={e => setV("blocked_start_months", e.target.value.split(/[,\s]+/).filter(Boolean))} className="w-full border-b border-[#E5E7EB] py-1" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="border border-[#E5E7EB] p-3 bg-white">
          <div className="label-eyebrow mb-1">"From" address — quotes</div>
          <input data-testid="setting-from-quotes" value={s.email_from_quotes || ""} onChange={e => setV("email_from_quotes", e.target.value)} className="w-full border-b border-[#E5E7EB] py-1" />
        </div>
        <div className="border border-[#E5E7EB] p-3 bg-white">
          <div className="label-eyebrow mb-1">"From" address — bookings &amp; reminders</div>
          <input data-testid="setting-from-bookings" value={s.email_from_bookings || ""} onChange={e => setV("email_from_bookings", e.target.value)} className="w-full border-b border-[#E5E7EB] py-1" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 mt-3">
        <div className="border border-[#E5E7EB] p-3 bg-white flex items-center justify-between gap-4">
          <div>
            <div className="label-eyebrow mb-1">Auto-notify clients of new photos</div>
            <p className="text-xs text-[#6B7280] max-w-xl">When on, clients are automatically emailed whenever you publish new build-progress photos — capped at one email every 24 hours so a batch of uploads only sends once. The manual "Notify client" button always sends immediately.</p>
          </div>
          <label className="inline-flex items-center cursor-pointer shrink-0">
            <input data-testid="setting-photo-auto-notify" type="checkbox" checked={s.photo_auto_notify !== false} onChange={e => setV("photo_auto_notify", e.target.checked)} className="sr-only peer" />
            <span className="relative w-11 h-6 bg-[#D1D5DB] peer-checked:bg-[#C79A3B] rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5"></span>
          </label>
        </div>
      </div>
      <button data-testid="admin-save-settings" className="de-btn-primary mt-8" onClick={() => onSave(s)}>Save settings</button>

      <div className="mt-12 border-t border-[#E5E7EB] pt-8">
        <div className="label-eyebrow mb-1">Configurator item names</div>
        <p className="text-xs text-[#6B7280] mb-4 max-w-2xl">Rename any add-on the customer sees in the quote configurator. These are display names only — the amounts charged are set in the Pricing panel at the top of this page.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(s.labels || {}).map(([k, v]) => (
            <div key={k} className="border border-[#E5E7EB] p-3 bg-white">
              <div className="label-eyebrow mb-1 capitalize">{k.replace(/_/g, " ")}</div>
              <input data-testid={`label-${k}`} value={v || ""} onChange={e => setLabel(k, e.target.value)} className="w-full border-b border-[#E5E7EB] py-1 text-sm" />
            </div>
          ))}
        </div>
        <button data-testid="admin-save-labels" className="de-btn-primary mt-6" onClick={() => onSave(s)}>Save settings</button>
      </div>
    </div>
  );
}

function PostcodeChips({ value, onChange }) {
  const [input, setInput] = useState("");
  const codes = value || [];
  const add = () => {
    const parts = input.split(/[,\s]+/).map(p => p.trim().toUpperCase()).filter(Boolean);
    if (!parts.length) return;
    const next = Array.from(new Set([...codes.map(c => c.toUpperCase()), ...parts]));
    onChange(next);
    setInput("");
  };
  const remove = (c) => onChange(codes.filter(x => x !== c));
  return (
    <div data-testid="postcode-chips">
      <div className="flex flex-wrap gap-2 mb-3">
        {codes.length === 0 && <span className="text-xs text-[#6B7280]">No postcodes yet — add the outward codes you serve (e.g. GU11, RG12).</span>}
        {codes.map(c => (
          <span key={c} data-testid={`postcode-chip-${c}`} className="inline-flex items-center gap-2 bg-[#F7E6DD] text-[#C79A3B] text-sm font-bold px-3 py-1 rounded-full">
            {c}
            <button type="button" data-testid={`postcode-remove-${c}`} onClick={() => remove(c)} className="text-[#A87E28] hover:text-[#12263A] leading-none text-base">×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input data-testid="postcode-input" value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Add outward code (e.g. GU11)" className="flex-1 border border-[#E5E7EB] px-3 py-2 text-sm" />
        <button type="button" data-testid="postcode-add" onClick={add} className="de-btn-secondary text-xs">Add</button>
      </div>
      <p className="text-xs text-[#6B7280] mt-2">Used by the postcode screener on the quote flow. Changes apply after you Save settings.</p>
    </div>
  );
}
